import AdvancedSearch from '@app/components/AdvancedSearch'
import BidirectionalSort from '@app/components/BidirectionalSort'
import InfiniteScrollTable from '@app/components/InfiniteScrollTable'
import SearchBar from '@app/components/SearchBar'
import { ISign } from '@app/models/Sign'
import { IGlobalConfigurationState } from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { humanize } from '@app/utils/formatting'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { RouteComponentProps } from 'react-router-dom'
import { Column } from 'react-virtualized'
import { Col, FormGroup, Input, Label } from 'reactstrap'
import { actionCreators, ISignsListState } from '../modules/signsList'

type ComponentProps = ISignsListState &
  IGlobalConfigurationState &
  typeof actionCreators &
  RouteComponentProps<{}>

export default class EarnestMoneyListPage extends React.PureComponent<ComponentProps> {
  private tableRef: React.RefObject<InfiniteScrollTable>

  constructor(props: ComponentProps) {
    super(props)

    bindFunctions(this)

    this.tableRef = React.createRef<InfiniteScrollTable>()
  }

  public render() {
    return (
      <DocumentTitle title='Reports - Signs'>
        <div className='container d-flex flex-column h-100 pt-3'>
          <div>
            <h3>Signs</h3>
            <SearchBar
              onChange={this._onChangeFilter}
              placeholder='Looking for a sign?'
              size='sm'
              value={this.props.filterText}
            />
            <AdvancedSearch className='mt-1' expand='md'>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Sort By</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.sortOrder}
                    onChange={this._onChangeSortOrder}
                  >
                    {this.props.configuration.signSortOptions
                      .map((p, i) => (
                        <option key={`sort-${i}`} value={p}>
                          {humanize(p)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <BidirectionalSort
                md={4}
                xl={3}
                orderBy={this.props.orderBy}
                changeOrdering={this._onChangeOrderBy}
              />
            </AdvancedSearch>
          </div>
          <div className='mt-4'>
            <InfiniteScrollTable
              isLoading={this.props.isLoading}
              items={this.props.items}
              loadItems={this.props.loadSigns}
              noRowsRenderer={this._noRowsRenderer}
              onRowClicked={this._onRowClicked}
              totalItems={this.props.totalItems}
              ref={this.tableRef}
            >
              <Column
                cellDataGetter={({ rowData }) => {
                  const sign = rowData as ISign
                  return sign.property.name
                }}
                dataKey='property'
                flexGrow={1}
                label='Property'
                width={100}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const sign = rowData as ISign

                  if (sign.property && sign.property.address) {
                    return `${sign.property.address.line1} ${sign.property.address.city},
                    ${sign.property.address.state} ${sign.property.address.zip}`
                  }

                  return null
                }}
                dataKey='address'
                headerClassName='d-none d-md-block'
                className='d-none d-md-block'
                flexGrow={1}
                label='Address'
                width={100}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const sign = rowData as ISign

                  return `${sign.quantity} | ${sign.signType.name}`
                }}
                dataKey='quantity'
                label='Quantity|Sign Type'
                width={150}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const sign = rowData as ISign

                  return sign.notes || '-'
                }}
                dataKey='notes'
                label='Notes'
                width={150}
              />
            </InfiniteScrollTable>
          </div>
        </div>
      </DocumentTitle>
    )
  }

  private _noRowsRenderer() {
    return <div>No signs</div>
  }

  private _onRowClicked(item: ISign, index: number) {
    window.open(
      `${window.location.pathname.replace(
        `admin/reports/signs`,
        `properties/property/${item.property.propertyId}`,
      )}`,
    )
  }

  private _onChangeFilter(newFilter: string) {
    this.props.setFilterText(newFilter)
    this._reloadData()
  }

  private _onChangeSortOrder(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setSortOrder(event.target.value)
    this._reloadData()
  }

  private _onChangeOrderBy(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setOrderBy(event.target.value)
    this._reloadData()
  }

  private _reloadData() {
    if (this.tableRef.current) {
      this.tableRef.current.forceReloadData()
    }
  }
}
